import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { FilterChoosePeriod } from "@/components/filter/FilterChoosePeriod.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { RecruitingReportingRecruitersSelector } from "@/pages/reporting/recruiting/components/RecruitingReportingRecruitersSelector.tsx";
import { RecruitingSourceEfficiencyReportTable } from "@/pages/reporting/recruiting/source-efficiency/RecruitingSourceEfficiencyReportTable.tsx";
import { useRecruitAnalyticsSourceEfficiencyReport } from "@/queries/analytics/use-recruit-analytics.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiAnalyticsRecruitingFilter } from "@/types/analytics/analytics.recruiting.types.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { Box, Page, Stack, Title } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export const RecruitingSourceEfficiencyReport = () => {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();

  useTitle(t("ats:report_source_efficiency_title"), t("common:reports"));

  const library = filtersLibrary();
  const {
    filter: { filter },
    config,
    filterButton,
  } = useAppFilter<ApiAnalyticsRecruitingFilter>({
    filters: [
      {
        ...library.period,
        defaultValue: "last_month_0",
      },
      {
        ...library.date_from,
        hideInFilter: true,
        name: "date_start",
      },
      {
        ...library.date_to,
        hideInFilter: true,
        name: "date_end",
      },
      {
        ...library.employee_ids,
        name: "recruiter_ids",
        label: t("ats:recruiters"),
      },
      library.job_ids,
      {
        ...library.employee_ids,
        name: "stakeholder_ids",
        label: t("ats:stakeholders"),
      },
    ],
    source: AnalyticReportTypes.recruitingSourceEfficiency,
  });

  const { data: report, isLoading } = useRecruitAnalyticsSourceEfficiencyReport(filter);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.reporting.viewed, {
      [analyticProperties.title]: AnalyticReportTypes.recruitingSourceEfficiency,
    });
  });

  return (
    <>
      <Box className={"bg-light h-full overflow-y-auto"}>
        <Page.Header title={t("ats:report_source_efficiency_title")}>
          <Stack direction={"horizontal"} gap={"sm"} className={"overflow-hidden"}>
            <FilterChoosePeriod filtersConfig={config} />

            {filterButton()}
          </Stack>
        </Page.Header>

        <Page.Content>
          <Box>
            <RecruitingReportingRecruitersSelector
              recruiterIds={filter.recruiter_ids || []}
              onChange={(recruiterIds) => {
                setSearchParams((searchParams) => {
                  searchParams.set("recruiter_ids", recruiterIds.join(","));
                  return searchParams;
                });
              }}
            />
          </Box>
          <Title
            header={t("ats:report_source_efficiency_table_title")}
            caption={t("ats:report_source_efficiency_table_description")}
            paddingTop
            paddingBottom
          />
          <RecruitingSourceEfficiencyReportTable report={report} isLoading={isLoading} />
        </Page.Content>
      </Box>
    </>
  );
};
